import { fancySocketBelongsToMarket } from '@/features/session/fancySocketScope'

type FancyHandler = (data: Record<string, unknown>) => void

const sseBase = (import.meta.env.VITE_COMMON_SOCKET_URL as string | undefined)?.replace(/\/$/, '') || ''

/**
 * Subscribe to Session-API / Socket-API fancy channels (SSE).
 * Replaces Lab Socket.IO joinRooms — Session-API blasts via betting-sse-hub `/v1/callSocket`.
 *
 * Use same-origin `VITE_COMMON_SOCKET_URL=/api/socket-api` (nginx/vite proxy).
 * Direct browser hits to betting-sse-hub.rimora.org fail (CORS allow-list + Helmet CORP).
 *
 * `preferBetfairId`: when hub sends Lab sendNewSocket arrays **or** single frames
 * on shared statusSocket_/Match Odds channels, only emit the matching fancy so
 * ScoreDashboard does not paint sibling market values onto the open row.
 */
export function subscribeFancyChannels(
  channels: string[],
  onData: FancyHandler,
  preferBetfairId?: string,
): () => void {
  const unique = [...new Set(channels.map((c) => c.trim()).filter(Boolean))]
  if (!sseBase || unique.length === 0) return () => undefined

  const url = `${sseBase}/v1/stream?channels=${encodeURIComponent(unique.join(','))}`
  const es = new EventSource(url)
  const wantId = preferBetfairId?.trim() || ''

  const emitOne = (payload: Record<string, unknown>) => {
    if (wantId && !fancySocketBelongsToMarket(payload, wantId)) return
    onData(payload)
  }

  const handle = (raw: MessageEvent) => {
    try {
      const parsed = JSON.parse(String(raw.data)) as {
        data?: Record<string, unknown> | Record<string, unknown>[]
        channel?: string
      }
      const payload = parsed?.data
      if (Array.isArray(payload)) {
        if (wantId) {
          const hit = payload.find(
            (row) =>
              row &&
              typeof row === 'object' &&
              fancySocketBelongsToMarket(row as Record<string, unknown>, wantId),
          ) as Record<string, unknown> | undefined
          if (hit) emitOne(hit)
          return
        }
        for (const row of payload) {
          if (row && typeof row === 'object' && !Array.isArray(row)) {
            emitOne(row as Record<string, unknown>)
          }
        }
        return
      }
      if (payload && typeof payload === 'object') {
        emitOne(payload as Record<string, unknown>)
      }
    } catch {
      // ignore malformed frames / heartbeats
    }
  }

  // Hub defaults to event name `message`; also listen unnamed + ping skip via handle try/catch.
  es.addEventListener('message', handle as EventListener)
  es.onmessage = handle

  es.onerror = () => {
    // EventSource auto-reconnects; avoid spamming console.
  }

  return () => {
    es.removeEventListener('message', handle as EventListener)
    es.onmessage = null
    es.onerror = null
    es.close()
  }
}
